/* Artist collection banner — scroll-snap carousel + description fold.
   No dependencies; native horizontal scrolling does the work, JS only syncs
   the dots/arrows and (optionally) auto-advances. */

class ArtistBannerCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-acb-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-acb-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-acb-dot]'));
    this.index = 0;

    // A single image needs no controls at all.
    if (this.slides.length < 2) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const prev = this.querySelector('[data-acb-prev]');
    const next = this.querySelector('[data-acb-next]');
    if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1, true));
    if (next) next.addEventListener('click', () => this.goTo(this.index + 1, true));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, true));
    });

    // Keyboard support while the track itself is focused.
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1, true);
      }
    });

    // The scroll position is the source of truth — swiping updates the dots too.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = this.slides.indexOf(entry.target);
          if (i !== -1) this.setActive(i);
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    this.setupAutoplay();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.stopAutoplay();
  }

  goTo(target, userInitiated) {
    const count = this.slides.length;
    const i = ((target % count) + count) % count; // wraps in both directions
    const slide = this.slides[i];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
    this.setActive(i);

    // A deliberate interaction should not fight the timer.
    if (userInitiated) this.restartAutoplay();
  }

  setActive(i) {
    this.index = i;
    this.dots.forEach((dot, n) => {
      dot.setAttribute('aria-selected', String(n === i));
    });
  }

  /* ── Autoplay ─────────────────────────────────────────────────────────── */

  setupAutoplay() {
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reducedMotion;
    this.autoplayInterval = parseInt(this.dataset.autoplayInterval, 10) || 6000;
    if (!this.autoplayEnabled) return;

    ['pointerenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.stopAutoplay()));
    ['pointerleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.startAutoplay()));

    this.visibilityHandler = () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.startAutoplay();
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.timer) return;
    this.timer = setInterval(() => this.goTo(this.index + 1, false), this.autoplayInterval);
  }

  stopAutoplay() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('artist-banner-carousel')) {
  customElements.define('artist-banner-carousel', ArtistBannerCarousel);
}

/* ── Description fold ───────────────────────────────────────────────────── */

function setupArtistBannerDescription() {
  document.querySelectorAll('.acb__description-toggle').forEach((btn) => {
    const wrapper = document.getElementById(btn.getAttribute('aria-controls'));
    if (!wrapper || wrapper.dataset.acbFoldReady === 'true') return;
    wrapper.dataset.acbFoldReady = 'true';

    // Only fold when the text is genuinely long (roughly 10+ lines).
    const lineHeight = parseFloat(getComputedStyle(wrapper).lineHeight) || 24;
    if (wrapper.scrollHeight <= lineHeight * 10) return;

    btn.style.display = '';
    wrapper.classList.add('is-collapsible');

    btn.addEventListener('click', () => {
      const isOpen = wrapper.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(isOpen));
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupArtistBannerDescription);
} else {
  setupArtistBannerDescription();
}

// Re-run when the theme editor re-renders the section.
document.addEventListener('shopify:section:load', setupArtistBannerDescription);
